/**
 * Store hours module (CU-14ym9bv7bbx).
 *
 * Turns one Stores metaobject's opening hours into a seven-day schedule and an open or
 * closed status, using the same rules as the checkout pickup date selector. All zone
 * maths goes through Intl.DateTimeFormat with the store's timezone; the window is UTC
 * calendar arithmetic. No dependencies, no Liquid.
 */
(function () {
  'use strict';

  var KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  var LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var TIME = /^([01][0-9]|2[0-3]):[0-5][0-9]$/;

  function toMs(now) {
    return typeof now === 'number' ? now : now.getTime();
  }

  function isObject(value) {
    return value !== null && typeof value === 'object';
  }

  function isPlainObject(value) {
    return isObject(value) && !Array.isArray(value);
  }

  function failure(reason, handle) {
    return { ok: false, reason: reason, handle: typeof handle === 'string' ? handle : '' };
  }

  var DATE_SHAPE = /^\d{4}-\d{2}-\d{2}$/;

  function hasExpectedShape(hours) {
    if (!isPlainObject(hours) || !isPlainObject(hours.days)) return false;
    var days = hours.days;
    for (var i = 0; i < KEYS.length; i++) {
      var day = days[KEYS[i]];
      if (day === undefined || day === null) continue;
      if (!isPlainObject(day) || !validTimes(day.open, day.close)) return false;
    }
    var overrides = hours.overrides;
    if (overrides === undefined || overrides === null) return true;
    if (!Array.isArray(overrides)) return false;
    for (var j = 0; j < overrides.length; j++) {
      var entry = overrides[j];
      if (!isPlainObject(entry)) return false;
      if (typeof entry.date !== 'string' || !DATE_SHAPE.test(entry.date)) return false;
      if (entry.type === 'CLOSED') continue;
      if (entry.type !== 'OPEN') return false;
      if (entry.open !== undefined && !(typeof entry.open === 'string' && TIME.test(entry.open))) return false;
      if (entry.close !== undefined && !(typeof entry.close === 'string' && TIME.test(entry.close))) return false;
      var weekday = days[weekdayKeyForDate(entry.date)];
      var base = isPlainObject(weekday) ? weekday : {};
      var open = entry.open !== undefined ? entry.open : base.open;
      var close = entry.close !== undefined ? entry.close : base.close;
      if (!validTimes(open, close)) return false;
    }
    return true;
  }

  function parsePayload(payload) {
    if (!isObject(payload) || typeof payload.handle !== 'string' || payload.handle === '') {
      return failure('store_not_found', isObject(payload) ? payload.handle : '');
    }
    var handle = payload.handle;
    var hours = payload.opening_hours;
    if (hours === null || hours === undefined || hours === '') {
      return failure('opening_hours_missing', handle);
    }
    if (typeof hours === 'string') {
      try {
        hours = JSON.parse(hours);
      } catch (e) {
        return failure('opening_hours_corrupt', handle);
      }
    }
    if (!hasExpectedShape(hours)) {
      return failure('opening_hours_invalid', handle);
    }
    var timezone = payload.timezone;
    if (typeof timezone !== 'string' || timezone === '') {
      return failure('timezone_missing', handle);
    }
    try {
      new Intl.DateTimeFormat('en-US', { timeZone: timezone });
    } catch (e) {
      return failure('timezone_invalid', handle);
    }
    return { ok: true, handle: handle, timezone: timezone, openingHours: hours };
  }

  function readPayload(root) {
    if (!root || typeof root.querySelector !== 'function') return null;
    try {
      var element = root.querySelector('script[data-store-hours-data]');
      if (!element) return null;
      return JSON.parse(element.textContent);
    } catch (e) {
      return null;
    }
  }

  function todayInTimezone(timezone, now) {
    var parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(toMs(now));
    function get(type) {
      for (var i = 0; i < parts.length; i++) {
        if (parts[i].type === type) return parts[i].value;
      }
      return '';
    }
    return get('year') + '-' + get('month') + '-' + get('day');
  }

  function buildWindow(timezone, now) {
    var bits = todayInTimezone(timezone, now).split('-').map(Number);
    var dates = [];
    for (var offset = 0; offset < 7; offset++) {
      dates.push(new Date(Date.UTC(bits[0], bits[1] - 1, bits[2] + offset)).toISOString().slice(0, 10));
    }
    return dates;
  }

  function dayIndex(dateStr) {
    var bits = dateStr.split('-').map(Number);
    return new Date(Date.UTC(bits[0], bits[1] - 1, bits[2])).getUTCDay();
  }

  function weekdayKeyForDate(dateStr) {
    return KEYS[dayIndex(dateStr)];
  }

  function weekdayLabelForDate(dateStr) {
    return LABELS[dayIndex(dateStr)];
  }

  function validTimes(open, close) {
    return (
      typeof open === 'string' &&
      typeof close === 'string' &&
      TIME.test(open) &&
      TIME.test(close) &&
      open < close
    );
  }

  function resolveDay(openingHours, dateStr, handle) {
    var key = weekdayKeyForDate(dateStr);
    var row = {
      date: dateStr,
      weekdayKey: key,
      weekdayLabel: weekdayLabelForDate(dateStr),
      state: 'closed',
      open: null,
      close: null
    };
    var hours = isObject(openingHours) ? openingHours : {};
    var overrides = Array.isArray(hours.overrides) ? hours.overrides : [];
    var override = null;
    for (var i = 0; i < overrides.length; i++) {
      var entry = overrides[i];
      if (isObject(entry) && entry.date === dateStr) {
        override = entry;
        break;
      }
    }
    var weekday = isObject(hours.days) && isObject(hours.days[key]) ? hours.days[key] : null;
    var open;
    var close;

    if (override) {
      // Cases 1 and 2: anything but exactly OPEN is Closed, without a warning.
      if (override.type !== 'OPEN') return row;
      // Cases 3 to 5: the override's own times, falling back to the weekday's.
      open = override.open || (weekday ? weekday.open : undefined);
      close = override.close || (weekday ? weekday.close : undefined);
    } else {
      // Case 6: no weekday entry means Closed.
      if (!weekday) return row;
      // Case 7.
      open = weekday.open;
      close = weekday.close;
    }

    // Case 8: validity.
    if (!validTimes(open, close)) {
      console.warn('[store-hours] invalid hours', handle, dateStr, open, close);
      return row;
    }
    row.state = 'open';
    row.open = open;
    row.close = close;
    return row;
  }

  function buildSchedule(openingHours, timezone, now, handle) {
    return buildWindow(timezone, now).map(function (date) {
      return resolveDay(openingHours, date, handle);
    });
  }

  function minutesNowInTimezone(timezone, now) {
    var parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23'
    }).formatToParts(toMs(now));
    var hour = 0;
    var minute = 0;
    for (var i = 0; i < parts.length; i++) {
      if (parts[i].type === 'hour') hour = Number(parts[i].value);
      if (parts[i].type === 'minute') minute = Number(parts[i].value);
    }
    return (hour % 24) * 60 + minute;
  }

  function toMinutes(hhmm) {
    var bits = hhmm.split(':');
    return Number(bits[0]) * 60 + Number(bits[1]);
  }

  function statusFor(schedule, timezone, now) {
    var today = schedule[0];
    if (today && today.state === 'open') {
      var minutes = minutesNowInTimezone(timezone, now);
      if (minutes < toMinutes(today.open)) return { kind: 'opens_today', open: today.open };
      if (minutes < toMinutes(today.close)) return { kind: 'open', close: today.close };
    }
    for (var i = 1; i < 7 && i < schedule.length; i++) {
      if (schedule[i].state === 'open') {
        return { kind: 'opens_on', weekdayLabel: schedule[i].weekdayLabel, open: schedule[i].open };
      }
    }
    return { kind: 'closed' };
  }

  function formatTime(hhmm) {
    var bits = hhmm.split(':');
    var hour = Number(bits[0]);
    var minutes = bits[1];
    var suffix = hour < 12 ? 'am' : 'pm';
    var h12 = hour % 12;
    if (h12 === 0) h12 = 12;
    return h12 + (minutes === '00' ? '' : ':' + minutes) + suffix;
  }

  function formatDayHours(day) {
    if (day.state !== 'open') return 'Closed';
    return formatTime(day.open) + '-' + formatTime(day.close);
  }

  function statusText(status) {
    if (status.kind === 'open') return 'Open now - Closes ' + formatTime(status.close);
    if (status.kind === 'opens_today') return 'Closed - Opens ' + formatTime(status.open);
    if (status.kind === 'opens_on') {
      return 'Closed - Opens ' + status.weekdayLabel + ' ' + formatTime(status.open);
    }
    return 'Closed';
  }

  function tableHtml(schedule) {
    var html =
      '<h4>Store Hours</h4><table class="store-hours-table"><thead><tr>' +
      '<th scope="col" class="day-col">Day</th>' +
      '<th scope="col" class="schedule-col">Opening Hours</th>' +
      '</tr></thead><tbody>';
    schedule.forEach(function (day) {
      var cell = day.state === 'open' ? formatDayHours(day) : '<strong>Closed</strong>';
      html +=
        '<tr><td class="day-col">' + day.weekdayLabel + '</td>' +
        '<td class="schedule-col">' + cell + '</td></tr>';
    });
    return html + '</tbody></table>';
  }

  function statusHtml(status) {
    return '<p><strong>' + statusText(status) + '</strong></p>';
  }

  // Behavioural copy of the checkout extension's localToUtcMs (availability.ts).
  function localToUtcMs(dateStr, timeStr, timezone) {
    var d = dateStr.split('-').map(Number);
    var t = timeStr.split(':').map(Number);
    var naiveUtc = Date.UTC(d[0], d[1] - 1, d[2], t[0], t[1], 0, 0);
    var parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23'
    }).formatToParts(new Date(naiveUtc));
    function get(type) {
      for (var i = 0; i < parts.length; i++) {
        if (parts[i].type === type) return Number(parts[i].value);
      }
      return 0;
    }
    var displayedUtc = Date.UTC(
      get('year'),
      get('month') - 1,
      get('day'),
      get('hour') % 24,
      get('minute'),
      get('second')
    );
    return naiveUtc + (naiveUtc - displayedUtc);
  }

  function nextRefreshDelayMs(schedule, timezone, now) {
    var nowMs = toMs(now);
    var targets = [localToUtcMs(schedule[1].date, '00:00', timezone)];
    var today = schedule[0];
    if (today && today.state === 'open') {
      targets.push(localToUtcMs(today.date, today.open, timezone));
      targets.push(localToUtcMs(today.date, today.close, timezone));
    }
    var earliest = null;
    targets.forEach(function (target) {
      if (target > nowMs && (earliest === null || target < earliest)) earliest = target;
    });
    // The single-pass localToUtcMs can place the next midnight up to an hour early near a daylight-saving
    // change, so with nothing ahead the page re-checks each minute until the real rollover.
    if (earliest === null) return 60000;
    return earliest - nowMs + 1000;
  }

  window.StoreHours = {
    parsePayload: parsePayload,
    readPayload: readPayload,
    todayInTimezone: todayInTimezone,
    buildWindow: buildWindow,
    weekdayKeyForDate: weekdayKeyForDate,
    weekdayLabelForDate: weekdayLabelForDate,
    resolveDay: resolveDay,
    buildSchedule: buildSchedule,
    minutesNowInTimezone: minutesNowInTimezone,
    statusFor: statusFor,
    formatTime: formatTime,
    formatDayHours: formatDayHours,
    statusText: statusText,
    tableHtml: tableHtml,
    statusHtml: statusHtml,
    nextRefreshDelayMs: nextRefreshDelayMs
  };
})();
